import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject, computedFrom } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import PerfectScrollbar from 'perfect-scrollbar';
import { FormInformtionApi } from '~/api/form-information';

import { GenericApi } from '~/api/generic-api';
import { ProcedureApi } from '~/api/procedure-api';
import { AppRouteID } from '~/app-routes';
import { AppState } from '~/app-store';
import { BaseViewModel } from '~/base-view-model';
import { FormInfoDialog } from '~/components/form-info-dialog/form-info-dialog';
import { NavigationActions } from '~/components/navigation/navigation-actions';
import {
	generateBookmarkFromRouter,
	isBookmarkEqual,
} from '~/components/navigation/navigation-utils';
import { GlobalEvents, ShortcutKey } from '~/constants';
import { PageController } from '~/models/page-controller';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import { HelpFileUtils } from '~/utils/help-file-utils';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Logger } from '~/utils/logger';
import { AuditApi } from '~/api/audit-api';
import { AuditDialog, AuditDialogModel } from '~/components/audit/audit-dialog';
import { BlockUtils } from '~/utils/block-utils';

@autoinject
export class MainContent extends BaseViewModel {
	private readonly addBookmarkText: string;

	private readonly removeBookmarkText: string;

	private readonly showHelp: string;

	private readonly showProcedure: string;

	private readonly showFormInfo: string;

	private readonly showFormAudit: string;

	/**
	 * The default page title, based on the route title for the form.
	 * This is used on pages/routes that don't have a "pageHeading" property.
	 */
	private readonly defaultPageTitle: string;

	private procedureFilePath: string;

	private psContainer: PerfectScrollbar;

	private currentController: PageController | undefined;

	private readonly refContainer: HTMLElement;
	private readonly refHeaderTitle: HTMLElement;

	constructor(
		protected readonly store: Store<StateHistory<AppState>>,
		protected readonly router: Router,
		protected readonly eventAggregator: EventAggregator,
		protected readonly i18n: I18N,
		private readonly dialogUtils: DialogUtils,
		private readonly element: Element,
		private readonly auditApi: AuditApi,
	) {
		super();
		this.addBookmarkText = i18n.tr('main-content.add-bookmark');
		this.removeBookmarkText = i18n.tr('main-content.remove-bookmark');
		this.showHelp = i18n.tr('main-content.show-help');
		this.showProcedure = i18n.tr('main-content.show-procedure');
		this.showFormInfo = i18n.tr('main-content.show-form-info');
		this.showFormAudit = i18n.tr('main-content.show-form-audit');

		this.defaultPageTitle = this.i18n.tr(
			router.currentInstruction.config.title,
		);
	}

	private bind(): void {
		this.subscribeToStateChanges();
		this.subscribeToEvent(GlobalEvents.Scroll.Top, this.scrollTop);
	}

	private activate(): void {
		// NOTE(Jae): 2020-02-17 - WEBUI-3083
		// If you want to know how on Earth I found this, its called an educated guess!
		// The guess was that Aurelia probably stores what controller/class you're currently on somewhere, and I just
		// explored the tree of objects in Chrome Dev Tools.
		// ie. console.warn(this), kept opening nested objects, CTRL+F for the class name.
		// If that guess turned out to be incorrect, my alternative would have been using the eventAggregator to tell this class
		// what the current controller is!
		this.currentController =
			this.router.currentInstruction.viewPortInstructions.default.component.viewModel;
	}

	private attached(): void {
		if (!this.refContainer) {
			throw new Error(
				'Unexpected error. Missing element: a-app__main__content__container.',
			);
		}

		if (!this.refHeaderTitle) {
			throw new Error(
				'Unexpected error. Missing element: a-app__main__content__header__title.',
			);
		}

		if (this.router?.currentInstruction?.queryParams?.useArchive) {
			this.refHeaderTitle.querySelector('h1').classList.add('archive');
		} else {
			this.refHeaderTitle.querySelector('h1').classList.remove('archive');
		}

		// NOTE(Albert): 2020-09-25 - WEBUI-3642
		// Initialising PS too soon can cause it to be set on the refContainer before it is fully loaded
		// This affects forms like Transaction Centre which contain two Card components within a single page-content
		// slot. This causes a bug where the vertical PS initally appears without any viewport resize and then
		// disappears once you scroll down and up because it has updated itself with the correct length
		window.requestAnimationFrame(() => {
			this.psContainer = new PerfectScrollbar(this.refContainer, {
				suppressScrollX: true,
			});
		});
		KeyboardShortcut.subscribe(this, ShortcutKey.ProcedureFile, () =>
			this.openProcedureFile(),
		);
		KeyboardShortcut.subscribe(this, ShortcutKey.HelpFile, () =>
			this.openHelpFile(),
		);

		// Get procedure file
		{
			let formName = '';
			if (GenericApi.formName) {
				formName = GenericApi.formName;
			}
			if (
				!formName &&
				this.router &&
				this.router.currentInstruction &&
				this.router.currentInstruction.config &&
				this.router.currentInstruction.config.name
			) {
				formName = this.router.currentInstruction.config.name;
			}
			if (formName) {
				const params = this.router.currentInstruction.params;
				const formSubtype: string | undefined =
					params && params.formSubtype
						? params.formSubtype
						: undefined;

				if (
					formSubtype &&
					(formSubtype.includes('/') || formSubtype.includes('\\'))
				) {
					return;
				}

				ProcedureApi.getProcedureFile(formName, formSubtype)
					.then((data) => {
						if (data.status === 404) {
							this.procedureFilePath = undefined;
							Logger.warn(FetchUtils.normalizeError(data));
						} else {
							this.procedureFilePath = data;
						}
					})
					.catch((error) => {
						this.procedureFilePath = undefined;
						FetchUtils.logAndDisplayError(error);
					});
			}
		}
	}

	private detached() {
		if (this.psContainer) {
			this.psContainer.destroy();
			this.psContainer = undefined;
		}
		KeyboardShortcut.unsubscribeAll(this);
	}

	private scrollTop = (eventData: ScrollToOptions): void => {
		if (eventData.top !== null && eventData.top !== undefined) {
			this.refContainer.scrollTop = eventData.top;
		}
	};

	/**
	 * Toggle bookmark
	 */
	private onClickBookmarkButton(event): void {
		const bookmark = generateBookmarkFromRouter(this.router);
		Logger.log('Toggle bookmark:', bookmark);
		this.dispatch(NavigationActions.ToggleBookmark, bookmark);
	}

	private onClickShowFormInformation(event): void {
		let classicFormName;
		new Promise<string>((resolve, reject) => {
			resolve(this.getHelpFunctionName());
		})
			.then((helpFunctionName) => {
				return FormInformtionApi.getFormInformation(
					helpFunctionName,
					this.router.currentInstruction.params,
				).then((formInformation) => {
					classicFormName = formInformation.classicFormName;
				});
			})
			.finally(() => {
				this.dialogUtils.showCustomDialog(FormInfoDialog, {
					formName: GenericApi.formName,
					customisationName: GenericApi.customisationName,
					// based on Java logic in CustomisationUtils#makeFileName
					customisationFile:
						GenericApi.formName +
						(GenericApi.customisationName
							? `_${GenericApi.customisationName}`
							: ''),
					classicFormName,
				});
			});
	}

	private onClickShowFormAudit(event): void {
		BlockUtils.toggleAppInput(true);
		// change GenericApi.formName to this.currentController.functionName,
		// e.g. for Exception History form, GenericApi.formName is different from its API base
		// because it is modified in exception-history.ts
		AuditApi.getFormAudit(
			this.currentController.functionName,
			this.router.currentInstruction.queryString,
		)
			.then(() => {
				const auditDialogModel: AuditDialogModel = {
					functionName: this.currentController.functionName,
					queryString: this.router.currentInstruction.queryString,
					auditApi: this.auditApi,
					columnNames: this.currentController.formAuditColumnNames,
				};
				this.dialogUtils.showCustomDialog(
					AuditDialog,
					auditDialogModel,
				);
			})
			.catch((e) => {
				FetchUtils.logAndDisplayError(e);
			})
			.finally(() => {
				BlockUtils.toggleAppInput(false);
			});
	}

	/**
	 * canBeBookmarked will determine if a page can be bookmarked or not.
	 * By default, a page will be bookmarkable, so to disable bookmarking, you
	 * must make "canBeBookmarked" property equal "false".
	 */
	@computedFrom('showNavigation', 'currentController.canBeBookmarked')
	get canBeBookmarked(): boolean {
		if (!this.showNavigation) {
			// Disable bookmarking if rendering from TreeView
			// This is because there is no side-navigation for forms when
			// loaded in the app.
			return false;
		}
		if (!this.currentController) {
			return false;
		}
		const canBeBookmarked = this.currentController.canBeBookmarked;
		if (canBeBookmarked === false) {
			return false;
		}
		return true;
	}

	/**
	 * If the form hasAuditDetails, then shows the form audit button
	 */
	@computedFrom('currentController.enableFormAudit')
	get canBeFormAudited(): boolean {
		if (!this.currentController) {
			return false;
		}
		return this.currentController.enableFormAudit;
	}

	@computedFrom(
		'currentController.pageHeading',
		'currentController.customisationConfig.functionConfig.title',
	)
	get pageHeading(): string | undefined {
		let heading: string | undefined;
		if (
			this.currentController &&
			this.currentController.pageHeading !== undefined
		) {
			heading = this.currentController.pageHeading;
		}
		if (
			heading === undefined &&
			this.currentController &&
			this.currentController.customisationConfig &&
			this.currentController.customisationConfig.functionConfig &&
			this.currentController.customisationConfig.functionConfig.title !==
				null &&
			this.currentController.customisationConfig.functionConfig.title !==
				undefined
		) {
			heading = this.i18n.tr(
				this.currentController.customisationConfig.functionConfig.title,
			);
		}
		if (heading === undefined) {
			heading = this.defaultPageTitle;
		}

		if (this.router?.currentInstruction?.queryParams?.useArchive) {
			heading += this.i18n.tr('archive.title-suffix');
		}

		// NOTE(Jae): 2020-02-17 - WEBUI-
		// Bit of a hack to force the <title> tag to be changed when this
		// pageHeading gets updated.
		// This should make it relatively painless to update the page <title>
		// though / keep it synced to the heading.
		this.router.currentInstruction.config.navModel.setTitle(heading);

		return heading;
	}

	@computedFrom(
		'currentController.pageSubHeading',
		'currentController.customisationConfig.functionConfig.subHeading',
	)
	get pageSubHeading(): string | undefined {
		let subHeading: string | undefined;
		if (this.currentController && this.currentController.pageSubHeading) {
			subHeading = this.currentController.pageSubHeading;
		}
		if (
			subHeading === undefined &&
			this.currentController &&
			this.currentController.customisationConfig &&
			this.currentController.customisationConfig.functionConfig &&
			this.currentController.customisationConfig.functionConfig
				.subHeading !== null &&
			this.currentController.customisationConfig.functionConfig
				.subHeading !== undefined
		) {
			subHeading = this.i18n.tr(
				this.currentController.customisationConfig.functionConfig
					.subHeading,
			);
		}
		return subHeading;
	}

	/**
	 * Used by view
	 * To be consistent, `Show help` button should trigger F1 keydown in case openHelpFile event is overwritten.
	 * ie, adviser-authority-details subscribes a different openHelpFile handler at member level
	 */
	private triggerHelpFileShortcut(): void {
		this.element.dispatchEvent(
			new KeyboardEvent('keydown', { key: ShortcutKey.HelpFile[0] }),
		);
	}

	private getHelpFunctionName() {
		let functionName: string = '';
		if (GenericApi.formName) {
			functionName = GenericApi.formName;
		}
		if (typeof this.currentController.helpFileFunctionName === 'function') {
			const customFunctionName =
				this.currentController.helpFileFunctionName();
			if (customFunctionName !== undefined) {
				functionName = customFunctionName;
			}
		}
		if (!functionName) {
			throw new Error('Cannot determine functionName for help file.');
		}
		return functionName;
	}

	private openHelpFile(): void {
		let functionName: string = this.getHelpFunctionName();
		if (typeof this.currentController.helpFileFunctionName === 'function') {
			const customFunctionName =
				this.currentController.helpFileFunctionName();
			if (customFunctionName !== undefined) {
				functionName = customFunctionName;
			}
		}

		HelpFileUtils.openHelpFileIfPossible(
			functionName,
			this.router.currentInstruction.params,
		);
	}

	private openProcedureFile(): void {
		if (!this.procedureFilePath) {
			return;
		}
		window.open(GenericApi.apiServerURL + this.procedureFilePath);
	}

	/**
	 * Is the current page bookmarked
	 */
	@computedFrom(
		'appState.present.navigation.bookmarks',
		'router.currentInstruction.config.route',
	)
	get isBookmarked(): boolean {
		if (
			!this.appState ||
			!this.appState.present ||
			!this.appState.present.navigation ||
			!this.appState.present.navigation.bookmarks
		) {
			return false;
		}
		const currentPathBookmark = generateBookmarkFromRouter(this.router);
		for (const bookmark of this.appState.present.navigation.bookmarks) {
			if (isBookmarkEqual(currentPathBookmark, bookmark)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Is the current page blank. Used to hide the header buttons on a blank page
	 */
	@computedFrom('router.currentInstruction.config.route')
	get isBlankPage(): boolean {
		return this.router.currentInstruction.config.route === AppRouteID.Blank;
	}

	@computedFrom('router.currentInstruction.config.settings.errorMessage')
	private get errorMessage(): string | undefined {
		if (
			!this.router ||
			!this.router.currentInstruction ||
			!this.router.currentInstruction.config ||
			!this.router.currentInstruction.config.settings ||
			!this.router.currentInstruction.config.settings.errorMessage
		) {
			return undefined;
		}
		const errorMessage =
			this.router.currentInstruction.config.settings.errorMessage.trim();
		return errorMessage;
	}

	@computedFrom('errorMessage')
	private get errorMessageUnsafeHtml(): string {
		if (!this.errorMessage) {
			return '';
		}
		return this.errorMessage.replace('\n', '<br/>');
	}
}
